import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useConfig } from '../contexts/ConfigContext';
import { submitPilotRequest } from '../services/admin';
import { ArrowRight, CheckCircle2, ShieldCheck } from 'lucide-react';

const COMPANY_TYPES = ['Importer', 'Exporter', 'Freight Forwarder', 'Compliance Team', 'Other'];

export default function RequestAccess() {
  const { config, loading: cfgLoading } = useConfig();
  const [form, setForm] = useState({
    company_name: '', contact_name: '', email: '',
    company_type: 'Importer', country: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  if (!cfgLoading && config && !config.enable_pilot_requests) {
    return <Navigate to="/" replace />;
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await submitPilotRequest(form);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Could not submit request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="min-h-screen bg-navy-950 hero-grid-bg py-16 md:py-24 px-6">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8" data-testid="back-to-home">
          ← Back home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-slate-300 mb-6 backdrop-blur-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-cyan-glow animate-pulse" />
            Private Pilot Program · Invite-only
          </div>

          <h1 className="font-display text-3xl md:text-5xl font-bold text-white tracking-tight leading-tight">
            Request pilot access
          </h1>
          <p className="mt-4 text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl">
            ComplyTradeIQ is currently in private pilot with selected import/export teams.
            Tell us about your operation and our compliance team will be in touch.
          </p>
        </motion.div>

        <div className="mt-10 rounded-2xl border border-white/10 bg-navy-900/70 backdrop-blur-xl p-7 md:p-9 shadow-2xl">
          {done ? (
            <div className="flex gap-4" data-testid="pilot-success">
              <CheckCircle2 className="h-6 w-6 text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <h2 className="font-display text-xl font-bold text-white">Request received</h2>
                <p className="text-slate-300 mt-2 leading-relaxed">
                  Our compliance team will review your request and reach out within two business days.
                  You can track new pilots from the Internal Operations Console once admitted.
                </p>
                <Link to="/" className="mt-5 inline-flex items-center gap-2 text-cyan-glow hover:text-cyan-hover text-sm">
                  Back to landing page <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="pilot-form">
              {error && (
                <div className="md:col-span-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="pilot-error">
                  {error}
                </div>
              )}

              <Field label="Company name" value={form.company_name} onChange={set('company_name')} testid="pilot-company" />
              <Field label="Contact name" value={form.contact_name} onChange={set('contact_name')} testid="pilot-contact" />
              <Field label="Business email" type="email" value={form.email} onChange={set('email')} testid="pilot-email" />
              <Field label="Country" value={form.country} onChange={set('country')} placeholder="e.g. Finland" testid="pilot-country" />
              <Select label="Company type" value={form.company_type} onChange={set('company_type')} options={COMPANY_TYPES} testid="pilot-type" className="md:col-span-2" />
              <TextArea label="Message (optional)" value={form.message} onChange={set('message')} testid="pilot-message" className="md:col-span-2"
                placeholder="Trade lanes, product categories, current document workflow…" />

              <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                <div className="flex items-center gap-2 text-xs text-slate-500 font-mono">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Information used only for pilot evaluation. No marketing contact.
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-cyan-glow text-navy-950 font-semibold transition-all glow-cyan hover:bg-cyan-hover hover:scale-[1.02] disabled:opacity-60"
                  data-testid="pilot-submit-btn"
                >
                  {submitting ? 'Submitting…' : (<>Request access <ArrowRight className="h-4 w-4" /></>)}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="mt-8 text-xs text-slate-500 font-mono tracking-widest uppercase text-center">
          AI interprets · Rules decide · Humans act
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder = '', testid, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs text-slate-400 uppercase tracking-wider font-mono">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required
        placeholder={placeholder}
        className="mt-2 w-full bg-navy-950 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder-slate-600 focus:border-cyan-glow/50 focus:outline-none focus:ring-2 focus:ring-cyan-glow/20 transition-colors"
        data-testid={testid}
      />
    </label>
  );
}

function Select({ label, value, onChange, options, testid, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs text-slate-400 uppercase tracking-wider font-mono">{label}</span>
      <select value={value} onChange={onChange}
        className="mt-2 w-full bg-navy-950 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-cyan-glow/50 focus:outline-none focus:ring-2 focus:ring-cyan-glow/20"
        data-testid={testid}>
        {options.map(o => <option key={o}>{o}</option>)}
      </select>
    </label>
  );
}

function TextArea({ label, value, onChange, testid, className = '', placeholder = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs text-slate-400 uppercase tracking-wider font-mono">{label}</span>
      <textarea value={value} onChange={onChange} rows={4} placeholder={placeholder}
        className="mt-2 w-full bg-navy-950 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder-slate-600 focus:border-cyan-glow/50 focus:outline-none focus:ring-2 focus:ring-cyan-glow/20 resize-none"
        data-testid={testid} />
    </label>
  );
}
